import { z } from 'zod';
import { addDays } from './numbering';
import { dayDiff } from './planning';
import { addMonths } from './service';
import { plPlural } from './notifications';

/**
 * Flota — samochody firmowe (docs/19-flota.md). Moduł opcjonalny: wyłączony = uprawnienia `modul.flota` i `flota.*`
 * odbierane w profilu (menu, ekrany, API, aplikacja mobilna, przypomnienia), dane zostają.
 */

export const FLEET_MODULE = 'flota';
/** uprawnienia należące do modułu (wyłączony moduł → odbierane w profilu) */

export const VEHICLE_STATUSES = { aktywny: 'Aktywny', wycofany: 'Wycofany' } as const;
export type VehicleStatus = keyof typeof VEHICLE_STATUSES;
export const VEHICLE_OWNERSHIP = { wlasny: 'Własny', leasing: 'Leasing', wynajem: 'Wynajem' } as const;
export type VehicleOwnership = keyof typeof VEHICLE_OWNERSHIP;
export const VEHICLE_FUELS = { benzyna: 'Benzyna', diesel: 'Diesel', lpg: 'Benzyna + LPG', hybryda: 'Hybryda', elektryczny: 'Elektryczny', inny: 'Inny' } as const;
export type VehicleFuel = keyof typeof VEHICLE_FUELS;

export const OBLIGATION_RULES = { data: 'Data', przebieg: 'Przebieg', wczesniej: 'Data lub przebieg — co nastąpi wcześniej' } as const;
export type ObligationRule = keyof typeof OBLIGATION_RULES;
export const OBLIGATION_STATUSES = { otwarta: 'Do wykonania', umowiona: 'Umówiona', wykonana: 'Wykonana', anulowana: 'Anulowana' } as const;
export type ObligationStatus = keyof typeof OBLIGATION_STATUSES;
export const RECURRENCE_BASIS = { wykonanie: 'od wykonania', termin: 'od poprzedniego terminu' } as const;
export type RecurrenceBasis = keyof typeof RECURRENCE_BASIS;
export const OBLIGATION_SOURCES = { reczna: 'Ręcznie', dokument: 'Z dokumentu', zalecenie: 'Zalecenie serwisu' } as const;
export type ObligationSource = keyof typeof OBLIGATION_SOURCES;

export const VISIT_STATUSES = { planowana: 'Planowana', w_trakcie: 'W trakcie', zakonczona: 'Zakończona', odwolana: 'Odwołana' } as const;
export type VisitStatus = keyof typeof VISIT_STATUSES;

export const ODOMETER_SOURCES = { paliwo: 'Transakcja paliwowa', serwis: 'Serwis', badanie: 'Badanie techniczne', zgloszenie: 'Zgłoszenie użytkownika', reczny: 'Wpis zarządzającego', import: 'Import' } as const;
export type OdometerSource = keyof typeof ODOMETER_SOURCES;
export const ODOMETER_STATES = { ok: 'Potwierdzony', do_weryfikacji: 'Do weryfikacji', odrzucony: 'Odrzucony' } as const;
export type OdometerState = keyof typeof ODOMETER_STATES;

export const REPORT_CATEGORIES = { usterka: 'Usterka', opony: 'Opony', serwis: 'Serwis', inne: 'Inne' } as const;
export type ReportCategory = keyof typeof REPORT_CATEGORIES;
export const REPORT_STATUSES = {
  nowe: 'Nowe',
  w_analizie: 'W analizie',
  do_realizacji: 'Do realizacji',
  serwis_zaplanowany: 'Serwis zaplanowany',
  w_realizacji: 'W realizacji',
  zakonczone: 'Zakończone',
  odrzucone: 'Odrzucone',
} as const;
export type ReportStatus = keyof typeof REPORT_STATUSES;
export const REPORT_OPEN: readonly ReportStatus[] = ['nowe', 'w_analizie', 'do_realizacji', 'serwis_zaplanowany', 'w_realizacji'];

export const TYRE_SEASONS = { letnie: 'Letnie', zimowe: 'Zimowe', caloroczne: 'Całoroczne' } as const;
export type TyreSeason = keyof typeof TYRE_SEASONS;
export const TYRE_STATES = { zalozone: 'Założone', przechowywane: 'Przechowywane', wycofane: 'Wycofane' } as const;
export type TyreState = keyof typeof TYRE_STATES;
export const TYRE_NEEDS = { brak: 'Brak', wymiana: 'Wymiana', zakup: 'Zakup' } as const;
export type TyreNeed = keyof typeof TYRE_NEEDS;
export const TYRE_OPS = { zalozenie: 'Założenie', zdjecie: 'Zdjęcie', przechowanie: 'Przekazanie do przechowania', wycofanie: 'Wycofanie', zmiana: 'Zmiana danych' } as const;
export type TyreOp = keyof typeof TYRE_OPS;

export const UNAVAILABILITY_REASONS = { serwis: 'Serwis', naprawa: 'Naprawa', szkoda: 'Szkoda', inne: 'Inne' } as const;
export type UnavailabilityReason = keyof typeof UNAVAILABILITY_REASONS;

/** dokumenty wrażliwe (polisy, umowy) — tylko z uprawnieniem do kosztów */
export const VEHICLE_DOC_CATEGORIES = { dowod: 'Dowód rejestracyjny', polisa: 'Polisa', umowa: 'Umowa leasingu / wynajmu', protokol: 'Protokół serwisu', zdjecie: 'Zdjęcie', inne: 'Inne' } as const;
export type VehicleDocCategory = keyof typeof VEHICLE_DOC_CATEGORIES;
export const SENSITIVE_DOC_CATEGORIES: readonly VehicleDocCategory[] = ['polisa', 'umowa'];

export const FLEET_COST_CATEGORIES = { paliwo: 'Paliwo', serwis: 'Serwis', naprawy: 'Naprawy', opony: 'Opony', ubezpieczenie: 'Ubezpieczenie', leasing: 'Leasing / wynajem', pozostale: 'Pozostałe' } as const;
export type FleetCostCategory = keyof typeof FLEET_COST_CATEGORIES;
/** z faktury (udokumentowany), ręczny, przewidywany — raporty nie sumują ich bez kontroli */
export const FLEET_COST_KINDS = { faktura: 'Z faktury', reczny: 'Ręczny', przewidywany: 'Przewidywany' } as const;
export type FleetCostKind = keyof typeof FLEET_COST_KINDS;

export const FUEL_TX_KINDS = { paliwo: 'Paliwo', myjnia: 'Myjnia', plyny: 'Płyny i oleje', oplaty: 'Opłaty', inne: 'Inne' } as const;
export type FuelTxKind = keyof typeof FUEL_TX_KINDS;
export const FUEL_MATCH_STATUSES = { dopasowana: 'Dopasowana', do_wyjasnienia: 'Do wyjaśnienia', niejednoznaczna: 'Niejednoznaczna', pominieta: 'Pominięta' } as const;
export type FuelMatchStatus = keyof typeof FUEL_MATCH_STATUSES;

// ── ustawienia floty (app_settings „flota”) ─────────────────────────────

export interface FleetSettings {
  /** dni przed terminem, w których przypominać (np. 30 i 7) */
  dueDays: number[];
  /** km przed limitem, przy których przypominać */
  dueKm: number[];
  /** odczyt starszy niż tyle dni — nieaktualny (czynności kilometrowe: „brak aktualnych danych”) */
  staleDays: number;
  /** przyrost km na dobę uznawany za podejrzany */
  maxKmPerDay: number;
  /** koszt km: odczyt musi być najdalej tyle dni od początku i końca okresu */
  costPerKmToleranceDays: number;
  /** rodzaje / przeznaczenie pojazdów */
  vehicleKinds: string[];
  /** konflikty niedostępności z planem: ile dni naprzód pokazywać w panelu */
  conflictDays: number;
}

export const DEFAULT_FLEET_SETTINGS: FleetSettings = {
  dueDays: [30, 7],
  dueKm: [2000, 500],
  staleDays: 45,
  maxKmPerDay: 1500,
  costPerKmToleranceDays: 15,
  vehicleKinds: ['Montażowy', 'Handlowy', 'Osobowy', 'Inny'],
  conflictDays: 14,
};

const thresholds = z.array(z.number().int().positive().max(100_000)).max(5);
export const fleetSettingsSchema = z.object({
  dueDays: thresholds,
  dueKm: thresholds,
  staleDays: z.number().int().min(1).max(730),
  maxKmPerDay: z.number().int().min(50).max(10_000),
  costPerKmToleranceDays: z.number().int().min(0).max(120),
  vehicleKinds: z.array(z.string().trim().min(1).max(40)).min(1).max(20),
  conflictDays: z.number().int().min(1).max(90),
});
export const fleetSettings = (stored: Partial<FleetSettings> | null | undefined): FleetSettings => ({ ...DEFAULT_FLEET_SETTINGS, ...(stored ?? {}) });

// ── daty ────────────────────────────────────────────────────────────────

const DAY_MS = 86_400_000;

// ── stan czynności (termin / przebieg / co wcześniej) ──────────────────

export const OBLIGATION_STATES = { po_terminie: 'Po terminie', blisko: 'Zbliża się', brak_danych: 'Brak aktualnych danych', ok: 'W porządku', bez_terminu: 'Bez terminu' } as const;
export type ObligationState = keyof typeof OBLIGATION_STATES;
const SEVERITY: Record<ObligationState, number> = { po_terminie: 4, blisko: 3, brak_danych: 2, ok: 1, bez_terminu: 0 };

export interface OdometerLike {
  km: number;
  readAt: string;
}

export interface ObligationEval {
  state: ObligationState;
  daysLeft: number | null;
  kmLeft: number | null;
  /** ostatni odczyt jest nieaktualny (czynność kilometrowa) */
  stale: boolean;
  /** przekroczony próg przypomnienia (do identyfikatora powiadomienia): „d30”, „d7”, „k500”, „po”, „bd” */
  level: string | null;
  /** co zdecydowało o stanie */
  reason: string;
}

/**
 * Stan czynności na dziś. Przebieg oceniany wyłącznie z potwierdzonego odczytu; odczyt starszy niż próg nie daje
 * stanu „w porządku” — czynność kilometrowa ma wtedy „brak aktualnych danych” (stary odczyt nie daje fałszywego bezpieczeństwa).
 */
export function evaluateObligation(
  o: { rule: ObligationRule; dueDate: string | null; dueKm: number | null },
  odo: OdometerLike | null,
  today: string,
  s: Pick<FleetSettings, 'dueDays' | 'dueKm' | 'staleDays'>,
): ObligationEval {
  const useDate = o.rule !== 'przebieg' && o.dueDate !== null;
  const useKm = o.rule !== 'data' && o.dueKm !== null;
  let dateState: ObligationState = 'bez_terminu';
  let kmState: ObligationState = 'bez_terminu';
  let daysLeft: number | null = null;
  let kmLeft: number | null = null;
  let dateLevel: string | null = null;
  let kmLevel: string | null = null;
  let stale = false;
  const maxDays = Math.max(0, ...s.dueDays);
  const maxKm = Math.max(0, ...s.dueKm);

  if (useDate) {
    daysLeft = dayDiff(today, o.dueDate!);
    if (daysLeft < 0) {
      dateState = 'po_terminie';
      dateLevel = 'po';
    } else if (daysLeft <= maxDays) {
      dateState = 'blisko';
      dateLevel = `d${Math.min(...s.dueDays.filter((t) => daysLeft! <= t))}`;
    } else dateState = 'ok';
  }
  if (useKm) {
    if (!odo) {
      kmState = 'brak_danych';
      kmLevel = 'bd';
      stale = true;
    } else {
      kmLeft = o.dueKm! - odo.km;
      stale = dayDiff(odo.readAt.slice(0, 10), today) > s.staleDays;
      if (kmLeft <= 0) {
        kmState = 'po_terminie';
        kmLevel = 'po';
      } else if (kmLeft <= maxKm) {
        kmState = 'blisko';
        kmLevel = `k${Math.min(...s.dueKm.filter((t) => kmLeft! <= t))}`;
      } else if (stale) {
        kmState = 'brak_danych';
        kmLevel = 'bd';
      } else kmState = 'ok';
    }
  }
  const worstKm = SEVERITY[kmState] > SEVERITY[dateState];
  const state = worstKm ? kmState : dateState;
  const level = worstKm ? kmLevel : dateLevel;
  const reason =
    state === 'bez_terminu'
      ? 'brak terminu i limitu'
      : worstKm
        ? kmState === 'brak_danych'
          ? odo
            ? `ostatni odczyt z ${odo.readAt.slice(0, 10)} — nieaktualny`
            : 'brak odczytu przebiegu'
          : `${kmLeft! <= 0 ? 'limit przekroczony o' : 'do limitu'} ${Math.abs(kmLeft!)} km${stale ? ' (odczyt nieaktualny)' : ''}`
        : `${daysLeft! < 0 ? 'po terminie' : 'do terminu'} ${Math.abs(daysLeft!)} ${plPlural(Math.abs(daysLeft!), 'dzień', 'dni', 'dni')}`;
  return { state, daysLeft, kmLeft, stale: useKm && stale, level, reason };
}

/** Następny termin czynności cyklicznej po wykonaniu (reguła tej czynności — bez wspólnych założeń). */
export function nextOccurrence(
  o: { dueDate: string | null; dueKm: number | null; everyMonths: number | null; everyKm: number | null; basis: RecurrenceBasis },
  done: { date: string; km: number | null },
): { dueDate: string | null; dueKm: number | null } | null {
  if (!o.everyMonths && !o.everyKm) return null;
  const fromDate = o.basis === 'termin' && o.dueDate ? o.dueDate : done.date;
  const fromKm = o.basis === 'termin' && o.dueKm !== null ? o.dueKm : done.km;
  return {
    dueDate: o.everyMonths ? addMonths(fromDate, o.everyMonths) : null,
    dueKm: o.everyKm && fromKm !== null ? fromKm + o.everyKm : null,
  };
}

/**
 * Weryfikacja nowego odczytu względem potwierdzonych (wg daty odczytu, nie daty zapisu): spadek względem wcześniejszego,
 * wyższy niż późniejszy albo nierealny przyrost → powód; null — odczyt wiarygodny.
 */
export function odometerIssue(confirmed: readonly OdometerLike[], r: OdometerLike, maxKmPerDay: number): string | null {
  const t = Date.parse(r.readAt);
  let before: OdometerLike | null = null;
  let after: OdometerLike | null = null;
  for (const c of confirmed) {
    const ct = Date.parse(c.readAt);
    if (ct <= t && (!before || ct > Date.parse(before.readAt))) before = c;
    if (ct > t && (!after || ct < Date.parse(after.readAt))) after = c;
  }
  if (before && r.km < before.km) return `Przebieg niższy niż odczyt z ${before.readAt.slice(0, 10)} (${before.km} km)`;
  if (after && r.km > after.km) return `Przebieg wyższy niż późniejszy odczyt z ${after.readAt.slice(0, 10)} (${after.km} km)`;
  if (before) {
    const days = Math.max(1, (t - Date.parse(before.readAt)) / DAY_MS);
    if ((r.km - before.km) / days > maxKmPerDay) return `Nierealny przyrost: ${r.km - before.km} km w ${Math.round(days)} dni`;
  }
  return null;
}

/** Ostatni potwierdzony odczyt (wg daty odczytu). */
export function latestReading<T extends OdometerLike>(confirmed: readonly T[]): T | null {
  let best: T | null = null;
  for (const c of confirmed) if (!best || Date.parse(c.readAt) > Date.parse(best.readAt) || (c.readAt === best.readAt && c.km > best.km)) best = c;
  return best;
}

/**
 * Przebieg w okresie — tylko gdy są potwierdzone odczyty blisko początku i końca okresu (tolerancja w dniach).
 * Bez interpolacji poza odczyty: brak danych → null (koszt km nie jest wtedy liczony).
 */
export function kmInPeriod(confirmed: readonly OdometerLike[], from: string, to: string, toleranceDays: number): { km: number; fromReading: OdometerLike; toReading: OdometerLike } | null {
  const near = (day: string) =>
    confirmed
      .filter((c) => Math.abs(dayDiff(c.readAt.slice(0, 10), day)) <= toleranceDays)
      .sort((a, b) => Math.abs(dayDiff(a.readAt.slice(0, 10), day)) - Math.abs(dayDiff(b.readAt.slice(0, 10), day)))[0] ?? null;
  const a = near(from);
  const b = near(to);
  if (!a || !b || Date.parse(b.readAt) <= Date.parse(a.readAt) || b.km <= a.km) return null;
  return { km: b.km - a.km, fromReading: a, toReading: b };
}

/** Numer rejestracyjny w postaci porównywalnej (bez spacji i myślników, wielkie litery). */
export const normalizePlate = (p: string) => p.replace(/[\s-]/g, '').toUpperCase();

// ── wejście (formularze) ────────────────────────────────────────────────

const uuid = z.string().uuid();
const day = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Data w formacie RRRR-MM-DD');
const str = (max: number) => z.string().trim().max(max);
const optStr = (max: number) => z.string().trim().max(max).default('');
const km = z.number().int().min(0).max(5_000_000);
const gr = z.number().int().min(-100_000_000_00).max(100_000_000_00);

export const vehicleSchema = z.object({
  companyId: uuid,
  plate: z.string().trim().min(2).max(15),
  make: optStr(60),
  model: optStr(60),
  vin: z.string().trim().toUpperCase().max(17).default(''),
  kind: optStr(40),
  fuel: z.enum(Object.keys(VEHICLE_FUELS) as [VehicleFuel, ...VehicleFuel[]]).nullable().default(null),
  ownership: z.enum(Object.keys(VEHICLE_OWNERSHIP) as [VehicleOwnership, ...VehicleOwnership[]]).default('wlasny'),
  financier: optStr(120),
  contractNumber: optStr(60),
  useFrom: day.nullable().default(null),
  useTo: day.nullable().default(null),
  note: optStr(2000),
});
export type VehicleInput = z.input<typeof vehicleSchema>;
export const vehicleUpdateSchema = vehicleSchema.omit({ plate: true });
export type VehicleUpdateInput = z.input<typeof vehicleUpdateSchema>;

/** zmiana numeru rejestracyjnego — ten sam pojazd, historia numerów */
export const plateChangeSchema = z.object({ plate: z.string().trim().min(2).max(15), validFrom: day, note: optStr(300) });
export const vehicleRetireSchema = z.object({ date: day, reason: str(500).min(3, 'Podaj powód wycofania') });

export const assignmentSchema = z
  .object({
    holder: z.enum(['osoba', 'ekipa']),
    userId: uuid.nullable().default(null),
    teamId: uuid.nullable().default(null),
    /** pusty przy ekipie — lider ekipy */
    responsibleId: uuid.nullable().default(null),
    validFrom: day,
    validTo: day.nullable().default(null),
    comment: optStr(500),
  })
  .refine((v) => (v.holder === 'osoba' ? !!v.userId : !!v.teamId), { message: 'Wskaż osobę albo ekipę', path: ['holder'] })
  .refine((v) => !v.validTo || v.validTo >= v.validFrom, { message: 'Data „do” nie może być wcześniejsza niż „od”', path: ['validTo'] });
export type AssignmentInput = z.input<typeof assignmentSchema>;
export const assignmentEndSchema = z.object({ validTo: day, comment: optStr(500) });
/** zmiana lidera ekipy: potwierdzenie nowego odpowiedzialnego (nowy wpis historii) albo pozostawienie obecnego */
export const leaderConfirmSchema = z.object({ decision: z.enum(['nowy_lider', 'bez_zmian']), validFrom: day, comment: optStr(500) });

export const activityTypeSchema = z.object({
  name: z.string().trim().min(2).max(80),
  rule: z.enum(Object.keys(OBLIGATION_RULES) as [ObligationRule, ...ObligationRule[]]).default('data'),
  /** podpowiedź cyklu — nie jest stosowana automatycznie, dopóki zarządzający jej nie przyjmie w czynności */
  everyMonths: z.number().int().min(1).max(240).nullable().default(null),
  everyKm: z.number().int().min(100).max(1_000_000).nullable().default(null),
  active: z.boolean().default(true),
});
export type ActivityTypeInput = z.input<typeof activityTypeSchema>;

const obligationBase = z.object({
  typeId: uuid,
  rule: z.enum(Object.keys(OBLIGATION_RULES) as [ObligationRule, ...ObligationRule[]]),
  dueDate: day.nullable().default(null),
  dueKm: km.nullable().default(null),
  everyMonths: z.number().int().min(1).max(240).nullable().default(null),
  everyKm: z.number().int().min(100).max(1_000_000).nullable().default(null),
  basis: z.enum(['wykonanie', 'termin']).default('wykonanie'),
  source: z.enum(['reczna', 'dokument', 'zalecenie']).default('reczna'),
  note: optStr(1000),
});
const obligationCheck = (v: { rule: ObligationRule; dueDate: string | null; dueKm: number | null }) =>
  (v.rule === 'data' && v.dueDate !== null) || (v.rule === 'przebieg' && v.dueKm !== null) || (v.rule === 'wczesniej' && (v.dueDate !== null || v.dueKm !== null));
export const obligationSchema = obligationBase.refine(obligationCheck, { message: 'Podaj termin lub limit przebiegu zgodny ze sposobem wyznaczania', path: ['dueDate'] });
export type ObligationInput = z.input<typeof obligationSchema>;
export const obligationUpdateSchema = obligationBase.omit({ typeId: true }).refine(obligationCheck, { message: 'Podaj termin lub limit przebiegu zgodny ze sposobem wyznaczania', path: ['dueDate'] });
/** przesunięcie terminu — nie jest wykonaniem */
export const obligationRescheduleSchema = z.object({ dueDate: day.nullable(), dueKm: km.nullable(), reason: optStr(500) });
export const obligationCompleteSchema = z.object({
  doneDate: day,
  doneKm: km.nullable().default(null),
  comment: optStr(1000),
  /** false — bez następnej czynności mimo cyklu (np. koniec użytkowania) */
  scheduleNext: z.boolean().default(true),
});
export type ObligationCompleteInput = z.input<typeof obligationCompleteSchema>;
export const obligationCancelSchema = z.object({ reason: str(500).min(3, 'Podaj powód') });

export const visitSchema = z
  .object({
    dateFrom: day,
    dateTo: day,
    workshop: optStr(200),
    note: optStr(1000),
    obligationIds: z.array(uuid).max(30).default([]),
    reportIds: z.array(uuid).max(30).default([]),
    /** pojazd niedostępny w czasie wizyty — wpis niedostępności */
    unavailable: z.boolean().default(true),
  })
  .refine((v) => v.dateTo >= v.dateFrom, { message: 'Data „do” nie może być wcześniejsza niż „od”', path: ['dateTo'] });
export type VisitInput = z.input<typeof visitSchema>;
export const visitStatusSchema = z.object({ status: z.enum(['planowana', 'w_trakcie', 'odwolana']), note: optStr(500) });
export const visitCompleteSchema = z.object({
  doneDate: day,
  km: km.nullable().default(null),
  /** czynności z wizyty faktycznie wykonane (pozostałe wracają do „do wykonania”) */
  doneObligationIds: z.array(uuid).max(30).default([]),
  /** zgłoszenia zakończone wizytą */
  doneReportIds: z.array(uuid).max(30).default([]),
  note: optStr(1000),
});
export type VisitCompleteInput = z.input<typeof visitCompleteSchema>;

export const odometerSchema = z.object({
  km,
  /** data i godzina odczytu (nie zapisu) */
  readAt: z.string().datetime({ offset: true }).or(day),
  source: z.enum(Object.keys(ODOMETER_SOURCES) as [OdometerSource, ...OdometerSource[]]).default('reczny'),
  note: optStr(300),
});
export type OdometerInput = z.input<typeof odometerSchema>;
export const odometerVerifySchema = z.object({ decision: z.enum(['ok', 'odrzucony']), note: optStr(300) });

export const reportSchema = z.object({
  vehicleId: uuid,
  category: z.enum(Object.keys(REPORT_CATEGORIES) as [ReportCategory, ...ReportCategory[]]),
  description: z.string().trim().min(3, 'Opisz problem').max(3000),
  /** ocena zgłaszającego: nie da się jeździć */
  blocksUse: z.boolean().default(false),
  km: km.nullable().default(null),
  photoIds: z.array(uuid).max(10).default([]),
});
export type ReportInput = z.input<typeof reportSchema>;
export const reportStatusSchema = z
  .object({ status: z.enum(Object.keys(REPORT_STATUSES) as [ReportStatus, ...ReportStatus[]]), note: optStr(1000) })
  .refine((v) => v.status !== 'odrzucone' || v.note.length >= 3, { message: 'Podaj uzasadnienie odrzucenia', path: ['note'] });

export const tyreSetSchema = z.object({
  season: z.enum(Object.keys(TYRE_SEASONS) as [TyreSeason, ...TyreSeason[]]),
  size: z.string().trim().min(3).max(30),
  brand: optStr(60),
  model: optStr(60),
  state: z.enum(Object.keys(TYRE_STATES) as [TyreState, ...TyreState[]]).default('przechowywane'),
  storage: optStr(200),
  need: z.enum(Object.keys(TYRE_NEEDS) as [TyreNeed, ...TyreNeed[]]).default('brak'),
  note: optStr(1000),
});
export type TyreSetInput = z.input<typeof tyreSetSchema>;
export const tyreOpSchema = z.object({
  op: z.enum(['zalozenie', 'zdjecie', 'przechowanie', 'wycofanie']),
  date: day,
  km: km.nullable().default(null),
  storage: optStr(200),
  note: optStr(500),
});

export const unavailabilitySchema = z
  .object({
    dateFrom: day,
    dateTo: day,
    reason: z.enum(Object.keys(UNAVAILABILITY_REASONS) as [UnavailabilityReason, ...UnavailabilityReason[]]),
    note: optStr(500),
    replacementVehicleId: uuid.nullable().default(null),
  })
  .refine((v) => v.dateTo >= v.dateFrom, { message: 'Data „do” nie może być wcześniejsza niż „od”', path: ['dateTo'] });
export type UnavailabilityInput = z.input<typeof unavailabilitySchema>;

export const vehicleDocMetaSchema = z.object({
  category: z.enum(Object.keys(VEHICLE_DOC_CATEGORIES) as [VehicleDocCategory, ...VehicleDocCategory[]]).default('inne'),
  description: optStr(300),
});

export const fleetCostSchema = z
  .object({
    category: z.enum(Object.keys(FLEET_COST_CATEGORIES) as [FleetCostCategory, ...FleetCostCategory[]]),
    kind: z.enum(['reczny', 'przewidywany']),
    costDate: day,
    netGr: gr,
    grossGr: gr,
    liters: z.number().min(0).max(100_000).nullable().default(null),
    obligationId: uuid.nullable().default(null),
    visitId: uuid.nullable().default(null),
    note: optStr(500),
  })
  .refine((v) => Math.abs(v.grossGr) >= Math.abs(v.netGr), { message: 'Brutto nie może być mniejsze niż netto', path: ['grossGr'] });
export type FleetCostInput = z.input<typeof fleetCostSchema>;

/** przypisanie pozycji (lub całej) faktury zakupu do pojazdów: każdy pojazd — część kwoty netto */
export const invoiceAllocationSchema = z.object({
  /** numer pozycji (lp) faktury; null — cała faktura */
  lineLp: z.number().int().min(1).nullable(),
  category: z.enum(Object.keys(FLEET_COST_CATEGORIES) as [FleetCostCategory, ...FleetCostCategory[]]),
  parts: z
    .array(z.object({ vehicleId: uuid, netGr: gr, liters: z.number().min(0).max(100_000).nullable().default(null), note: optStr(300) }))
    .min(1)
    .max(30),
});
export type InvoiceAllocationInput = z.input<typeof invoiceAllocationSchema>;

export const fleetCostReportQuerySchema = z.object({
  from: day,
  to: day,
  companyId: uuid.optional(),
  vehicleId: uuid.optional(),
});
export type FleetCostReportQuery = z.infer<typeof fleetCostReportQuerySchema>;

/**
 * Transakcja paliwowa w postaci wspólnej (po parserze konkretnego źródła). Parser ORLEN powstanie po otrzymaniu
 * próbek (docs/19 §9); model przyjmuje tylko to, co źródło faktycznie udostępnia.
 */
export const fuelTransactionRowSchema = z.object({
  externalId: z.string().trim().min(1).max(80),
  txAt: z.string().datetime({ offset: true }),
  plate: optStr(15),
  /** identyfikator karty bez pełnego numeru (np. ostatnie cyfry / numer wewnętrzny karty) */
  cardRef: optStr(40),
  kind: z.enum(Object.keys(FUEL_TX_KINDS) as [FuelTxKind, ...FuelTxKind[]]),
  productName: optStr(120),
  quantity: z.number().min(0).max(100_000).nullable().default(null),
  unit: optStr(10),
  netGr: gr,
  grossGr: gr,
  currency: z.string().length(3).default('PLN'),
  station: optStr(200),
  km: km.nullable().default(null),
  /** numer faktury zbiorczej / KSeF, jeśli źródło go podaje */
  invoiceRef: optStr(64),
});
export type FuelTransactionRow = z.input<typeof fuelTransactionRowSchema>;
export const fuelImportSchema = z.object({
  companyId: uuid,
  source: z.enum(['orlen']),
  /** identyfikator partii (np. sha256 pliku) — ten sam plik drugi raz nie tworzy nic nowego */
  sourceKey: z.string().trim().min(8).max(80),
  periodFrom: day.nullable().default(null),
  periodTo: day.nullable().default(null),
  rows: z.array(fuelTransactionRowSchema).min(1).max(20_000),
});
export type FuelImportInput = z.input<typeof fuelImportSchema>;
export const fuelMatchSchema = z.object({ vehicleId: uuid.nullable(), status: z.enum(['dopasowana', 'pominieta']), note: optStr(300) });
export const fuelCardSchema = z.object({ companyId: uuid, label: z.string().trim().min(1).max(60), cardRef: z.string().trim().min(2).max(40), vehicleId: uuid.nullable().default(null), validFrom: day });

// ── zgłoszenia z telefonu ───────────────────────────────────────────────

export const fieldFleetReportSchema = reportSchema;
export const fieldOdometerSchema = z.object({ vehicleId: uuid, km, readAt: z.string().datetime({ offset: true }).optional() });

// ── DTO ─────────────────────────────────────────────────────────────────

export interface FleetPersonRef {
  id: string;
  name: string;
}

export interface VehicleAssignmentDto {
  id: string;
  holder: 'osoba' | 'ekipa';
  user: FleetPersonRef | null;
  team: { id: string; name: string; leaderId: string | null } | null;
  responsible: FleetPersonRef;
  validFrom: string;
  validTo: string | null;
  comment: string;
  responsibleFromLeader: boolean;
  /** ekipa ma innego lidera niż odpowiedzialny z chwili przypisania — do potwierdzenia */
  leaderChanged: boolean;
  createdByName: string | null;
  createdAt: string;
}

export interface OdometerDto {
  id: string;
  km: number;
  readAt: string;
  source: OdometerSource;
  state: OdometerState;
  issue: string;
  note: string;
  recordedAt: string;
  recordedByName: string | null;
  verifiedByName: string | null;
}

export interface ObligationDto {
  id: string;
  vehicleId: string;
  plate: string;
  typeId: string;
  typeName: string;
  rule: ObligationRule;
  dueDate: string | null;
  dueKm: number | null;
  everyMonths: number | null;
  everyKm: number | null;
  basis: RecurrenceBasis;
  source: ObligationSource;
  status: ObligationStatus;
  visit: { id: string; dateFrom: string; dateTo: string; workshop: string; status: VisitStatus } | null;
  doneDate: string | null;
  doneKm: number | null;
  doneByName: string | null;
  note: string;
  previousId: string | null;
  eval: ObligationEval;
  costGr: number;
}

export interface VisitDto {
  id: string;
  vehicleId: string;
  plate: string;
  dateFrom: string;
  dateTo: string;
  workshop: string;
  status: VisitStatus;
  km: number | null;
  note: string;
  obligations: { id: string; typeName: string; status: ObligationStatus }[];
  reports: { id: string; number: string; status: ReportStatus }[];
  unavailabilityId: string | null;
}

export interface FleetReportDto {
  id: string;
  number: string;
  vehicleId: string;
  plate: string;
  reporter: FleetPersonRef;
  category: ReportCategory;
  description: string;
  blocksUse: boolean;
  km: number | null;
  status: ReportStatus;
  statusNote: string;
  visitId: string | null;
  photos: { id: string; url: string }[];
  history: { status: ReportStatus; note: string; at: string; byName: string | null }[];
  createdAt: string;
  updatedAt: string;
}

export interface TyreSetDto {
  id: string;
  vehicleId: string;
  season: TyreSeason;
  size: string;
  brand: string;
  model: string;
  state: TyreState;
  storage: string;
  need: TyreNeed;
  note: string;
  ops: { op: TyreOp; date: string; km: number | null; note: string; byName: string | null; at: string }[];
}

export interface UnavailabilityDto {
  id: string;
  vehicleId: string;
  plate: string;
  dateFrom: string;
  dateTo: string;
  reason: UnavailabilityReason;
  note: string;
  visitId: string | null;
  replacement: { id: string; plate: string } | null;
  cancelledAt: string | null;
  createdByName: string | null;
}

/** konflikt: pojazd niedostępny w dniu pracy ekipy (osoby), do której jest przypisany */
export interface FleetConflictDto {
  unavailabilityId: string;
  vehicleId: string;
  plate: string;
  reason: UnavailabilityReason;
  day: string;
  taskId: string;
  taskNumber: string;
  taskTitle: string;
  teamName: string | null;
  replacementPlate: string | null;
  text: string;
}

/**
 * docs/19 §6: auto niedostępne w dniach, w których jest przypisane do ekipy albo osoby — tablica planowania i karta zadania.
 * Jeden wpis na parę (niedostępność, przypisanie); `days` — dni w pytanym okresie. Informacja, bez blokady planowania.
 */
export interface FleetBlockDto {
  unavailabilityId: string;
  vehicleId: string;
  plate: string;
  reason: UnavailabilityReason;
  /** pełny okres niedostępności */
  dateFrom: string;
  dateTo: string;
  /** przypisanie auta w tych dniach: ekipa albo osoba */
  teamId: string | null;
  userId: string | null;
  replacementPlate: string | null;
  days: string[];
  /** „Auto WX 12345 niedostępne 03.11–05.11 (serwis)” */
  text: string;
}

/** „03.11” / „03.11–05.11” — okres niedostępności auta w ostrzeżeniach planowania */
export const fleetRangeText = (from: string, to: string) => {
  const dm = (d: string) => `${d.slice(8, 10)}.${d.slice(5, 7)}`;
  return from === to ? dm(from) : `${dm(from)}–${dm(to)}`;
};

export interface VehicleDocDto {
  id: string;
  category: VehicleDocCategory;
  description: string;
  fileName: string;
  mime: string;
  size: number;
  sensitive: boolean;
  createdAt: string;
  createdByName: string | null;
}

export interface FleetCostDto {
  id: string;
  vehicleId: string;
  plate: string;
  category: FleetCostCategory;
  kind: FleetCostKind;
  costDate: string;
  netGr: number;
  grossGr: number;
  liters: number | null;
  invoice: { id: string; number: string; sellerName: string; lineLp: number | null; lineName: string; rejected: boolean; kind: string } | null;
  transactionId: string | null;
  note: string;
  createdByName: string | null;
}

export interface VehicleListItemDto {
  id: string;
  companyId: string;
  companyName: string;
  plate: string;
  make: string;
  model: string;
  kind: string;
  status: VehicleStatus;
  assignment: { holder: 'osoba' | 'ekipa'; label: string; responsibleName: string; leaderChanged: boolean } | null;
  /** dziś niedostępny (przyczyna) */
  unavailable: { reason: UnavailabilityReason; dateTo: string } | null;
  next: { obligationId: string; typeName: string; state: ObligationState; reason: string; dueDate: string | null; dueKm: number | null } | null;
  odometer: { km: number; readAt: string; stale: boolean } | null;
  openReports: number;
  urgentReports: number;
}

export interface VehicleDto extends Omit<VehicleListItemDto, 'assignment'> {
  vin: string;
  fuel: VehicleFuel | null;
  ownership: VehicleOwnership;
  financier: string;
  contractNumber: string;
  useFrom: string | null;
  useTo: string | null;
  note: string;
  retiredAt: string | null;
  retireReason: string;
  plates: { plate: string; validFrom: string; validTo: string | null }[];
  assignment: VehicleAssignmentDto | null;
  can: { manage: boolean; costs: boolean };
}

export interface ActivityTypeDto {
  id: string;
  name: string;
  rule: ObligationRule;
  everyMonths: number | null;
  everyKm: number | null;
  active: boolean;
}

export interface FleetEventDto {
  at: string;
  action: string;
  text: string;
  byName: string | null;
}

/** Panel zarządzającego — działania do wykonania. */
export interface FleetDashboardDto {
  overdue: ObligationDto[];
  upcoming: ObligationDto[];
  /** czynności blisko/po terminie bez umówionej wizyty */
  toSchedule: ObligationDto[];
  nearKm: ObligationDto[];
  staleOdometer: { vehicleId: string; plate: string; km: number | null; readAt: string | null; obligations: number }[];
  odometerToVerify: { vehicleId: string; plate: string; readingId: string; km: number; readAt: string; issue: string }[];
  openReports: FleetReportDto[];
  unavailable: UnavailabilityDto[];
  noResponsible: { vehicleId: string; plate: string; reason: string }[];
  conflicts: FleetConflictDto[];
  tyreNeeds: { vehicleId: string; plate: string; setId: string; season: TyreSeason; need: TyreNeed }[];
  importIssues: { batchId: string; companyName: string; pending: number; createdAt: string }[];
}

export interface FleetCostReportDto {
  from: string;
  to: string;
  months: string[];
  vehicles: {
    vehicleId: string;
    plate: string;
    companyId: string;
    companyName: string;
    status: VehicleStatus;
    /** suma: z faktur + ręczne (przewidywane osobno, nie doliczane) */
    totalNetGr: number;
    invoiceNetGr: number;
    manualNetGr: number;
    plannedNetGr: number;
    byCategory: Record<string, number>;
    byMonth: Record<string, number>;
    fuelLiters: number;
    fuelNetGr: number;
    km: number | null;
    /** grosze na km — tylko przy wystarczających odczytach */
    costPerKmGr: number | null;
    kmNote: string;
  }[];
  companies: { companyId: string; companyName: string; totalNetGr: number; invoiceNetGr: number; manualNetGr: number; plannedNetGr: number }[];
  /** przypisania z faktur odrzuconych w Finansach — poza sumami */
  excludedRejectedGr: number;
}

export interface InvoiceFleetDto {
  invoiceId: string;
  rejected: boolean;
  /** pozycje faktury z kwotą przypisaną do pojazdów */
  lines: { lp: number; name: string; netGr: number | null; allocatedGr: number }[];
  wholeAllocatedGr: number;
  netGr: number;
  allocations: FleetCostDto[];
  /** korekta: pojazdy faktury korygowanej (podpowiedź) */
  correctedVehicles: { vehicleId: string; plate: string }[];
}

export interface FuelImportResultDto {
  batchId: string;
  duplicateBatch: boolean;
  added: number;
  skippedExisting: number;
  matched: number;
  pending: number;
}

export interface FuelTransactionDto {
  id: string;
  batchId: string;
  externalId: string;
  txAt: string;
  plate: string;
  cardRef: string;
  kind: FuelTxKind;
  productName: string;
  quantity: number | null;
  unit: string;
  netGr: number;
  grossGr: number;
  currency: string;
  station: string;
  km: number | null;
  invoiceRef: string;
  vehicle: { id: string; plate: string } | null;
  matchStatus: FuelMatchStatus;
  matchNote: string;
  invoiceId: string | null;
}

export interface FuelReconciliationDto {
  invoiceId: string;
  invoiceNumber: string;
  invoiceNetGr: number;
  transactionsNetGr: number;
  differenceGr: number;
  corrections: { id: string; number: string; netGr: number }[];
}

/** Użytkownik pojazdu (web „Mój samochód”, telefon „Zasoby → Samochody”) — bez kosztów, polis i terminów serwisowych. */
export interface MyVehicleDto {
  id: string;
  plate: string;
  make: string;
  model: string;
  companyName: string;
  holder: 'osoba' | 'ekipa';
  holderLabel: string;
  responsibleName: string;
  iAmResponsible: boolean;
  odometer: { km: number; readAt: string } | null;
  unavailable: { reason: UnavailabilityReason; dateTo: string } | null;
}

export interface MyFleetReportDto {
  id: string;
  number: string;
  vehicleId: string;
  plate: string;
  category: ReportCategory;
  description: string;
  blocksUse: boolean;
  status: ReportStatus;
  statusNote: string;
  createdAt: string;
  updatedAt: string;
}

export interface MyFleetDto {
  vehicles: MyVehicleDto[];
  reports: MyFleetReportDto[];
}

/** Ustawienia → Firmy: moduły opcjonalne (klucze niepodane — bez zmiany). */
export const systemModulesSchema = z.object({ flota: z.boolean().optional(), magazyn: z.boolean().optional() });
export type SystemModulesInput = z.infer<typeof systemModulesSchema>;
